import {
    Children, Fragment, cloneElement, isValidElement, useEffect,
} from 'react';
import { createPortal } from 'react-dom';
import { useInRouterContext, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowRightArrowLeft, faCalendarDays, faCircleInfo, faCircleStop, faCrosshairs, faDownload, faFileImport,
    faFloppyDisk, faForwardStep, faLink, faPause, faPenToSquare, faPercent, faPlay, faPlus, faRotateLeft, faSliders,
    faCheck, faTrashCan, faUserPlus, faXmark,
} from '@fortawesome/free-solid-svg-icons';

import { PrimaryButton, SecondaryButton, TertiaryButton } from '@/components/button/Button.jsx';
import { pageIconFor } from '@/routes/pageIcon.js';

import './Modal.css';

const cx = (...parts) => parts.filter(Boolean).join(' ');

let openCount = 0;

const Modal = ({
    isOpen,
    onClose,
    header,
    actions,
    children,
    size = 'medium',
    width,
    height,
    scrollbar = true,
    headerDivider = true,
    footerDivider = true,
    showCloseButton = true,
    closeOnOutsideClick = false,
    className,
}) => {
    useEffect(() => {
        if (!isOpen) return undefined;
        openCount += 1;
        document.body.style.overflow = 'hidden';
        return () => {
            openCount -= 1;
            if (!openCount) document.body.style.overflow = '';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const closeButton = showCloseButton && (
        <TertiaryButton
            className="modal-close"
            tone="neutral"
            icon={faXmark}
            aria-label="Close"
            onClick={onClose}
        />
    );

    return createPortal(
        <div
            className="modal-overlay"
            onClick={closeOnOutsideClick
                ? (e) => { if (e.target === e.currentTarget) onClose(); }
                : undefined}
        >
            <div
                className={cx('modal', `modal--${size}`, className)}
                style={width ? { '--modal-w': width } : undefined}
                role="dialog"
                aria-modal="true"
            >
                {header ? (
                    <div className={cx('modal-header', headerDivider && 'modal-header--divided')}>
                        {header}
                        {closeButton}
                    </div>
                ) : closeButton}
                {children && (
                    <div
                        className={cx('modal-content', !scrollbar && 'modal-content--no-scrollbar')}
                        style={height ? { height } : undefined}
                    >
                        {children}
                    </div>
                )}
                {actions && (
                    <div className={cx('modal-actions', footerDivider && 'modal-actions--divided')}>
                        {actions}
                    </div>
                )}
            </div>
        </div>,
        document.body,
    );
};

// Every dialog's header has an icon tile saying what the dialog is about. A dialog that names its own (a flask on New
// Experiment) keeps it; any other wears the sidebar icon of the page it opened on (Game Systems' on Add a System), so
// the header reads the same everywhere and a new dialog can't miss it. Off those pages it falls back to an info icon.
//
// Buttons say what they do: a dialog's main action gets an icon from its verb (a plus on Add System, a bin on
// Delete), else a check, unless the dialog gave it one. Cancel stays plain.
const ICON_BY_VERB = [
    [['new', 'create', 'add', 'define', 'register', 'duplicate'], faPlus],
    [['edit', 'rename', 'update', 'change'], faPenToSquare],
    [['delete', 'remove', 'discard', 'clear', 'revoke'], faTrashCan],
    [['reset', 'restore', 'revert', 'undo'], faRotateLeft],
    [['import', 'upload'], faFileImport],
    [['export', 'download'], faDownload],
    [['link', 'unlink'], faLink],
    [['start', 'run', 'test', 'resume', 'publish'], faPlay],
    [['save', 'confirm'], faFloppyDisk],
    [['skip'], faForwardStep],
    [['end', 'stop', 'cancel'], faCircleStop],
    [['pause'], faPause],
    [['switch', 'move'], faArrowRightArrowLeft],
    [['manage', 'set', 'settings', 'tags', 're-index'], faSliders],
    [['schedule'], faCalendarDays],
    [['rollout'], faPercent],
    [['targeting'], faCrosshairs],
    [['invite', 'resend'], faUserPlus],
];

const verbIcon = (text) => {
    const first = String(text || '').trim().split(/\s+/)[0]?.toLowerCase().replace(/[^a-z-]/g, '') || '';
    return ICON_BY_VERB.find(([words]) => words.includes(first))?.[1];
};

// Hooks need a router; a dialog in a test (or outside the app shell) has none and gets the fallback.
const usePathname = () => {
    const inRouter = useInRouterContext();
    // eslint-disable-next-line react-hooks/rules-of-hooks -- the router context never comes or goes for a mounted dialog
    return inRouter ? useLocation().pathname : '';
};

export const defaultModalIcon = (pathname) => pageIconFor(pathname) || faCircleInfo;

// For a dialog that draws its own header: the same icon the template would give it.
export const useDialogIcon = () => iconNode(defaultModalIcon(usePathname()));

const iconNode = (icon) => (isValidElement(icon) ? icon : <FontAwesomeIcon icon={icon} />);

// The dialog's own footer: its main (Primary) button gets an icon from its label when it has none.
const withActionIcons = (actions) => {
    const dress = (el) => {
        if (!isValidElement(el)) return el;
        // Into fragments and plain wrappers (a footer that groups its buttons in a span or div).
        if (el.type === Fragment || typeof el.type === 'string') {
            return el.props.children === undefined ? el : cloneElement(el, {}, Children.map(el.props.children, dress));
        }
        if (el.type !== PrimaryButton || el.props.icon !== undefined) return el;
        const label = Children.toArray(el.props.children).filter((c) => typeof c === 'string').join('').trim();
        const icon = label ? defaultActionIcon(label) : undefined;
        return icon ? cloneElement(el, { icon }) : el;
    };
    return dress(actions);
};

// A dialog's confirm button carries an icon too: from its verb ("Add System" a plus, "Delete" a bin), else a check.
export const defaultActionIcon = (label) => (typeof label === 'string' ? verbIcon(label) || faCheck : undefined);

export const StandardModal = ({
    icon,
    title,
    subtitle,
    status,
    onClose,
    onConfirm,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    confirmLoading = false,
    // Optional icon for the confirm button. Exists so a dialog whose confirm does something
    // other than "save and stay" can say so on the button itself — an arrow for a confirm that
    // navigates away, for instance. Omitted everywhere else, which leaves the button unchanged.
    confirmIcon,
    confirmIconPosition = 'left',
    // Corner badge for the confirm button — marks what the action is (e.g. that it navigates
    // away) while confirmIcon stays the action's own affordance. See Button's cornerIcon.
    confirmCornerIcon,
    actions,
    children,
    ...rest
}) => {
    const pathname = usePathname();
    return (
        <Modal
            {...rest}
            onClose={onClose}
            header={(icon || title || subtitle) && (
                <div className="modal-header-content">
                    {/* icon={false} leaves the tile out (side panels, full screen). */}
                    {icon !== false && (icon || title) && (
                        <span className="modal-icon">{icon || iconNode(defaultModalIcon(pathname))}</span>
                    )}
                    <div className="modal-header-text">
                        {title && <h2 className="modal-title">{title}</h2>}
                        {subtitle && <p className="modal-subtitle">{subtitle}</p>}
                    </div>
                </div>
            )}
            actions={actions ? withActionIcons(actions) : (onConfirm && (
                <>
                    <SecondaryButton status={status} onClick={onClose} disabled={confirmLoading}>
                        {cancelText}
                    </SecondaryButton>
                    <PrimaryButton
                        status={status}
                        onClick={onConfirm}
                        loading={confirmLoading}
                        icon={confirmIcon === undefined ? defaultActionIcon(confirmText) : confirmIcon}
                        iconPosition={confirmIconPosition}
                        cornerIcon={confirmCornerIcon}
                    >
                        {confirmText}
                    </PrimaryButton>
                </>
            ))}
        >
            {children}
        </Modal>
    );
};

export const AlertModal = ({
    status = 'neutral',
    showCloseButton = false,
    footerDivider = false,
    className,
    ...rest
}) => {
    const tone = status === 'neutral' ? undefined : status;

    return (
        <StandardModal
            {...rest}
            status={tone}
            showCloseButton={showCloseButton}
            footerDivider={footerDivider}
            className={cx(tone && `modal--status modal--${tone}`, className)}
        />
    );
};

export const SidePanel = ({ side = 'right', icon = false, className, ...rest }) => (
    <StandardModal {...rest} icon={icon} className={cx('modal--panel', `modal--panel-${side}`, className)} />
);

export const FullScreenModal = ({ icon = false, className, ...rest }) => (
    <StandardModal {...rest} icon={icon} className={cx('modal--fullscreen', className)} />
);

export default Modal;
